import React from 'react'
import PropTypes from 'prop-types'
import {
  Card, Table, Input, Typography, Space, Tag, Statistic, Row, Col,
  Select, DatePicker, Button, Dropdown, Menu
} from 'antd'
import {
  SearchOutlined, UserOutlined, CreditCardOutlined, CalendarOutlined,
  ShoppingCartOutlined, DownloadOutlined, PlusOutlined, MoreOutlined
} from '@ant-design/icons'
import {
  VoidTransactionModal,
  LinkTransactionModal,
  ManualTransactionModal
} from './TransactionModals'
import styled from 'styled-components'

const { Title, Text } = Typography

const StyledCard = styled(Card)`
  margin-bottom: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-radius: 8px;

  .ant-card-head {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-bottom: none;
    border-radius: 8px 8px 0 0;

    .ant-card-head-title {
      color: white;
      font-weight: 600;
    }
  }
`

const SearchContainer = styled.div`
  margin-bottom: 16px;
`

const StatCard = styled(Card)`
  text-align: center;
  height: 100%;

  &.total {
    border-left: 4px solid #1890ff;
  }

  &.active {
    border-left: 4px solid #52c41a;
  }

  &.expired {
    border-left: 4px solid #ff4d4f;
  }

  &.types {
    border-left: 4px solid #722ed1;
  }
`

const getTransactionTypeColor = (type) => {
  const colorMap = {
    'GOOGLE': 'green',
    'APPLE': 'blue',
    'ADMIN': 'purple',
    'FREE': 'orange',
    'PROMO': 'cyan'
  }
  return colorMap[type] || 'default'
}

const getPlanColor = (plan) => {
  const colorMap = {
    'premium': 'gold',
    'basic': 'blue',
    'free': 'default'
  }
  return colorMap[plan] || 'default'
}

const TRANSACTION_TYPES = [
  'google-in-app', 'google-in-app-refund', 'google-in-app-restore',
  'google-in-app-cancel', 'google-in-app-remove',
  'apple-in-app', 'apple-in-app-refund', 'apple-in-app-restore',
  'apple-in-app-cancel', 'apple-in-app-remove',
  'tele2-outer', 'outer-cancel', 'onevision-outer',
  'admin', 'admin-bonus', 'manual'
]

const UsersTransactions = ({
  transactions,
  loading,
  filters,
  onFiltersChange,
  pagination,
  onPaginationChange,
  onExport,
  exporting,
  onVoid,
  onLink,
  onCreateManual,
  mutating
}) => {
  const [voidTarget, setVoidTarget] = React.useState(null)
  const [linkTarget, setLinkTarget] = React.useState(null)
  const [manualOpen, setManualOpen] = React.useState(false)
  const columns = [
    {
      title: 'Пользователь',
      key: 'user',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Text strong>{record.user?.name || 'Не указано'}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.user?.username || 'Не указано'}
          </Text>
        </Space>
      ),
      width: '15%'
    },
    {
      title: 'План',
      key: 'plan',
      dataIndex: 'plan',
      render: (plan) => (
        <Tag color={getPlanColor(plan)} style={{ textTransform: 'uppercase' }}>
          {plan || 'Не указано'}
        </Tag>
      ),
      width: '10%'
    },
    {
      title: 'Period',
      key: 'period',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <CalendarOutlined style={{ color: '#52c41a' }} />
            <Text style={{ fontSize: '12px' }}>C: {record.createdAt}</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <CalendarOutlined style={{ color: '#ff4d4f' }} />
            <Text style={{ fontSize: '12px' }}>До: {record.expiresAt}</Text>
          </div>
        </Space>
      ),
      width: '15%'
    },
    {
      title: 'Тип оплаты',
      key: 'transactionType',
      dataIndex: 'transactionType',
      render: (type) => (
        <Tag color={getTransactionTypeColor(type)}>
          {type || 'Не указано'}
        </Tag>
      ),
      width: '12%'
    },
    {
      title: 'ID транзакций',
      key: 'transactionIds',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <div>
            <Text type="secondary" style={{ fontSize: '11px' }}>Vendor:</Text>
            <br />
            <Text
              style={{ fontSize: '11px', fontFamily: 'monospace' }}
              copyable={record.vendorTransactionId ? { text: record.vendorTransactionId } : false}
            >
              {record.vendorTransactionId || 'N/A'}
            </Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: '11px' }}>Original:</Text>
            <br />
            <Text
              style={{ fontSize: '11px', fontFamily: 'monospace' }}
              copyable={record.originalTransactionId ? { text: record.originalTransactionId } : false}
            >
              {record.originalTransactionId || 'N/A'}
            </Text>
          </div>
        </Space>
      ),
      width: '20%'
    },
    {
      title: 'Статус',
      key: 'status',
      render: (_, record) =>
        record.voided ? (
          <Tag color="red" title={record.voidReason || ''}>Аннулирована</Tag>
        ) : (
          <Tag color="green">Активна</Tag>
        ),
      width: '8%'
    },
    {
      title: '',
      key: 'actions',
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="link"
                disabled={!record.originalTransactionId}
                onClick={() => setLinkTarget(record)}
              >
                Привязать к пользователю
              </Menu.Item>
              <Menu.Item
                key="void"
                danger
                disabled={record.voided}
                onClick={() => setVoidTarget(record)}
              >
                Аннулировать
              </Menu.Item>
            </Menu>
          }
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
      width: '5%'
    }
  ]

  const getStats = () => {
    if (!transactions || transactions.length === 0) {
      return { total: 0, uniqueUsers: 0, transactionTypes: 0 }
    }

    const uniqueUsers = new Set(transactions.map(t => t.user?.id).filter(Boolean)).size
    const transactionTypes = new Set(transactions.map(t => t.transactionType).filter(Boolean)).size

    return {
      total: transactions.length,
      uniqueUsers,
      transactionTypes
    }
  }

  const stats = getStats()

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ color: '#1890ff', marginBottom: 8 }}>
          <ShoppingCartOutlined /> Транзакции пользователей
        </Title>
        <Text type="secondary">
          История покупок подписок и управление транзакциями
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <StatCard className="total">
            <Statistic
              title={
                <div>
                  <CreditCardOutlined style={{ color: '#1890ff' }} /> Всего транзакций
                </div>
              }
              value={stats.total}
              valueStyle={{ color: '#1890ff' }}
            />
          </StatCard>
        </Col>

        <Col span={6}>
          <StatCard className="active">
            <Statistic
              title={
                <div>
                  <UserOutlined style={{ color: '#52c41a' }} /> Уникальных пользователей
                </div>
              }
              value={stats.uniqueUsers}
              valueStyle={{ color: '#52c41a' }}
            />
          </StatCard>
        </Col>

        <Col span={6}>
          <StatCard className="types">
            <Statistic
              title="Типов оплаты"
              value={stats.transactionTypes}
              valueStyle={{ color: '#722ed1' }}
            />
          </StatCard>
        </Col>

        <Col span={6}>
          <StatCard className="expired">
            <Statistic
              title="Показано на странице"
              value={transactions?.length || 0}
              suffix={`/ ${pagination?.total || 0}`}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </StatCard>
        </Col>
      </Row>

      {/* Search */}
      <SearchContainer>
        <StyledCard title="Поиск и фильтры" size="small">
          <Space wrap style={{ width: '100%' }}>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Имя, телефон, ID юзера или ID транзакции..."
              value={filters.search}
              onChange={e => onFiltersChange({ search: e.target.value })}
              allowClear
              style={{ width: 320 }}
            />
            <Select
              placeholder="Тип оплаты"
              allowClear
              value={filters.transactionType}
              onChange={v => onFiltersChange({ transactionType: v || null })}
              options={TRANSACTION_TYPES.map(t => ({ value: t, label: t }))}
              style={{ width: 220 }}
            />
            <DatePicker.RangePicker
              value={filters.dateRange}
              onChange={range => onFiltersChange({ dateRange: range })}
              format="DD.MM.YYYY"
            />
            <Button
              icon={<DownloadOutlined />}
              onClick={onExport}
              loading={exporting}
            >
              Экспорт XLSX
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setManualOpen(true)}
            >
              Создать чек вручную
            </Button>
          </Space>
        </StyledCard>
      </SearchContainer>

      {/* Table */}
      <StyledCard title="Список транзакций">
        <Table
          dataSource={transactions}
          rowKey={item => item.id}
          columns={columns}
          pagination={{
            current: pagination?.current || 1,
            pageSize: pagination?.pageSize || 20,
            total: pagination?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} из ${total} транзакций`,
            onChange: onPaginationChange,
            onShowSizeChange: onPaginationChange
          }}
          loading={loading}
          scroll={{ x: 1000 }}
          size="small"
        />
      </StyledCard>

      {/* Help */}
      <Card style={{ marginTop: 16, background: '#f6ffed', borderColor: '#b7eb8f' }}>
        <Text type="secondary">
          💡 <strong>Подсказки:</strong> Кликните на ID транзакций чтобы скопировать их.
          Используйте поиск для быстрого нахождения конкретных транзакций или пользователей.
          Таблица поддерживает сортировку по всем колонкам.
          Экспорт выгружает все строки по текущему фильтру, а не только
          страницу. Если совпадений слишком много, придёт предупреждение,
          сколько строк выгружено из скольких — тогда сузьте диапазон дат.
        </Text>
      </Card>

      <VoidTransactionModal
        visible={!!voidTarget}
        transaction={voidTarget}
        loading={mutating}
        onCancel={() => setVoidTarget(null)}
        onOk={reason => {
          onVoid(voidTarget.id, reason)
          setVoidTarget(null)
        }}
      />
      <LinkTransactionModal
        visible={!!linkTarget}
        transaction={linkTarget}
        loading={mutating}
        onCancel={() => setLinkTarget(null)}
        onOk={userId => {
          onLink(linkTarget.originalTransactionId, userId)
          setLinkTarget(null)
        }}
      />
      <ManualTransactionModal
        visible={manualOpen}
        loading={mutating}
        onCancel={() => setManualOpen(false)}
        onOk={values => {
          onCreateManual(values)
          setManualOpen(false)
        }}
      />
    </div>
  )
}

UsersTransactions.propTypes = {
  transactions: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  filters: PropTypes.object.isRequired,
  onFiltersChange: PropTypes.func.isRequired,
  pagination: PropTypes.object.isRequired,
  onPaginationChange: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  exporting: PropTypes.bool,
  onVoid: PropTypes.func.isRequired,
  onLink: PropTypes.func.isRequired,
  onCreateManual: PropTypes.func.isRequired,
  mutating: PropTypes.bool
}

export default UsersTransactions